import type { Chart as ChartJS, Plugin } from 'chart.js';

const HOVERABLE_EVENTS = new Set(['mousemove', 'mouseout', 'mouseleave', 'click']);

type TooltipWithUpdate = {
  setActiveElements: (elements: { datasetIndex: number; index: number }[], position: { x: number; y: number }) => void;
  update: (mode?: boolean) => void;
};

function isRadarChart(chart: ChartJS): boolean {
  return (chart.config as { type?: string }).type === 'radar';
}

function getTooltip(chart: ChartJS): TooltipWithUpdate | undefined {
  return chart.tooltip as TooltipWithUpdate | undefined;
}

/** External tooltips only — block Chart.js from drawing the radial caret/spoke line. */
export const radarSuppressTooltipDrawPlugin: Plugin<'radar'> = {
  id: 'radarSuppressTooltipDraw',
  beforeTooltipDraw(chart) {
    if (!isRadarChart(chart)) return;
    return false;
  },
};

/** Pick exactly one radar vertex by pixel distance — never multiple active points. */
export const radarPrecisePointHoverPlugin: Plugin<'radar'> = {
  id: 'radarPrecisePointHover',
  afterEvent(chart, args) {
    if (!isRadarChart(chart)) return;

    const { event, replay, inChartArea } = args;
    const eventType = event.type as string;
    if (replay || !HOVERABLE_EVENTS.has(eventType)) return;

    const hideHover = () => {
      chart.setActiveElements([]);
      getTooltip(chart)?.setActiveElements([], { x: 0, y: 0 });
      getTooltip(chart)?.update(true);
      chart.draw();
    };

    if (!inChartArea || eventType === 'mouseout' || eventType === 'mouseleave' || eventType === 'click') {
      hideHover();
      return;
    }

    if (event.x == null || event.y == null) {
      hideHover();
      return;
    }

    const meta = chart.getDatasetMeta(0);
    if (!meta?.data?.length) {
      hideHover();
      return;
    }

    let closestIndex = -1;
    let closestDistanceSq = Number.POSITIVE_INFINITY;
    const maxHitDistance = 12;
    const maxHitDistanceSq = maxHitDistance * maxHitDistance;

    for (let index = 0; index < meta.data.length; index++) {
      const element = meta.data[index];
      if (element.x == null || element.y == null) continue;

      const dx = event.x - element.x;
      const dy = event.y - element.y;
      const distanceSq = dx * dx + dy * dy;

      if (distanceSq < closestDistanceSq) {
        closestDistanceSq = distanceSq;
        closestIndex = index;
      }
    }

    if (closestIndex === -1 || closestDistanceSq > maxHitDistanceSq) {
      hideHover();
      return;
    }

    const active = [{ datasetIndex: 0, index: closestIndex }];
    const position = { x: event.x, y: event.y };

    chart.setActiveElements(active);
    getTooltip(chart)?.setActiveElements(active, position);
    getTooltip(chart)?.update(true);
    chart.draw();
  },
};
